import { addDays, addMonths, differenceInDays, isSameDay, isSameYear, parse, startOfDay } from "date-fns";
import { useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { AssetBookingDto, BookableAssetDetailDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { ChevronLeftIcon } from "@/components/Icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { ClockIcon } from "@/components/Icons/ClockIcon";
import { buttonVariants } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { dayOfWeekIndex } from "@/helpers/date";
import { twResolve } from "@/helpers/tw-resolve";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { ToggleCalendarButton } from "@/modules/bookings/components/ToggleCalendarButton";
import { daysOptions } from "@/modules/bookings/constants";
import { useTranslation } from "@/translations";

import { getAllowedViewableDateRange } from "../helpers";
import type { AssetDetailContentProps } from "../pages/asset-detail/AssetDetailContent";

const MAX_AMOUNT_BOOKINGS_SHOWN = 3;
type Day = {
  date: Date;
  bookings: AssetBookingDto[];
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isDisabled?: boolean;
};
type CalendarMonthViewProps = {
  assetDetails: BookableAssetDetailDto;
  startDate: AssetDetailContentProps["startDate"];
  endDate: AssetDetailContentProps["endDate"];
  bookings: AssetDetailContentProps["bookings"];
  onChangeMonth: (date: Date) => void;
  onChangeView: (date?: Date) => void;
  onSelectBookings: (bookings: AssetBookingDto[]) => void;
};
export function CalendarMonthView({
  assetDetails,
  startDate,
  endDate,
  bookings,
  onChangeMonth,
  onChangeView,
  onSelectBookings,
}: CalendarMonthViewProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<Day>();
  const mobileBookingListRef = useRef<HTMLDivElement>(null);
  function isDisabledDay(day: Date) {
    if (assetDetails.availableFrom && day < startOfDay(new Date(assetDetails.availableFrom))) {
      return true;
    }
    if (assetDetails.publishAt && day < startOfDay(new Date(assetDetails.publishAt))) {
      return true;
    }
    if (assetDetails.maxDaysInAdvance != null && day > addDays(clockDate, assetDetails.maxDaysInAdvance)) {
      return true;
    }
    if (assetDetails.unpublishAt && day > new Date(assetDetails.unpublishAt)) {
      return true;
    }
    const bookableDay = assetDetails.bookableDays.find((x) => x.day === daysOptions[dayOfWeekIndex(day)]);
    if (bookableDay && !bookableDay.enabled) {
      return true;
    }

    return false;
  }
  const lastMonthDays = Array.from(
    {
      length: dayOfWeekIndex(startDate),
    },
    (_, i) => {
      const date = addDays(startDate, -dayOfWeekIndex(startDate) + i);

      return {
        date,
        bookings: [],
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
      } satisfies Day;
    },
  );
  const currentMonthDays = Array.from(
    {
      length: differenceInDays(endDate, startDate) + 1,
    },
    (_, i) => {
      const date = addDays(startDate, i);

      return {
        date,
        bookings: bookings.filter((x) => isSameDay(parse(x.date, "yyyy-MM-dd", new Date(0)), date)),
        isCurrentMonth: true,
        isDisabled: isDisabledDay(date),
        isSelected: false,
        isToday: isSameDay(addDays(startDate, i), clockDate),
      } satisfies Day;
    },
  );
  const nextMonthDays = Array.from(
    {
      length: 6 - dayOfWeekIndex(endDate),
    },
    (_, i) => {
      const date = addDays(endDate, i + 1);

      return {
        date,
        bookings: [],
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
      } satisfies Day;
    },
  );
  const days = [...lastMonthDays, ...currentMonthDays, ...nextMonthDays];
  const has6Rows = days.length > 35;
  function changeDates(goto: "next" | "previous") {
    if (goto === "next") {
      onChangeMonth(addMonths(startDate, 1));
    } else {
      onChangeMonth(addMonths(startDate, -1));
    }
  }
  function changeView() {
    onChangeView();
  }
  function selectDay(day: Day) {
    setSelectedDay(day);
    mobileBookingListRef?.current?.scrollIntoView({
      behavior: "smooth",
    });
  }
  const { minViewDate, maxViewDate } = getAllowedViewableDateRange(assetDetails);

  return (
    <section className="flex h-full flex-col gap-space-md">
      <header className="my-space-sm flex w-full flex-col-reverse items-center justify-center gap-space-md @md:my-0 @md:flex-row @md:justify-between">
        <Inline gap="sm" align="center" wrap={false}>
          <IconButton
            title={t("page.bookings.asset-detail.month-view.previous")}
            onClick={() => changeDates("previous")}
            disabled={startDate < minViewDate}
            size="sm"
            icon={<ChevronLeftIcon />}
          />
          <span className="min-w-control-narrow text-center capitalize">
            <FormattedDate date={startDate} format="month" />
            {!isSameYear(startDate, clockDate) && <span> {startDate.getFullYear()}</span>}
          </span>
          <IconButton
            disabled={endDate >= maxViewDate}
            title={t("page.bookings.asset-detail.month-view.next")}
            onClick={() => changeDates("next")}
            size="sm"
            icon={<ChevronRightIcon />}
          />
        </Inline>
        <ToggleCalendarButton isWeek={false} onToggleView={changeView} />
      </header>
      <section className="@md:flex @md:h-full @md:flex-col">
        <section className="ring-1 ring-border-default @sm:ring-0 @md:flex @md:flex-auto @md:flex-col">
          <header className="grid grid-cols-7 gap-px border-b border-border-secondary bg-background-secondary text-center text-caption text-text-secondary @md:flex-none">
            {daysOptions.map((option) => {
              const translation = t(`common.date.weekday.${option}`);

              return (
                <span key={option} className="bg-background-default py-space-sm @sm:py-space-md">
                  <span className="capitalize">{translation[0]}</span>
                  <span className="sr-only @sm:not-sr-only">{translation.slice(1, 3)}</span>
                  <span className="sr-only @md:not-sr-only">{translation.slice(3)}</span>
                </span>
              );
            })}
          </header>
          <section className="flex bg-background-secondary text-caption text-text-secondary @md:flex-auto">
            <section
              className={twJoin(
                "hidden w-full @md:grid @md:grid-cols-7 @md:gap-px",
                has6Rows ? "@md:grid-rows-6" : "@md:grid-rows-5",
              )}
            >
              {days.map((day) => (
                <div
                  key={day.date.toISOString()}
                  className={twJoin(
                    day.isCurrentMonth ? "bg-background-default" : "bg-background-default/20 text-text-secondary",
                    "relative min-h-[120px] px-1 py-2",
                  )}
                >
                  <time
                    dateTime={day.date.toISOString()}
                    className={twJoin(
                      "flex size-icon-prominent items-center justify-center",
                      day.isToday
                        ? "rounded-full bg-background-brand text-body-emphasis text-text-default-inverse"
                        : undefined,
                    )}
                  >
                    {day.date.getDate()}
                  </time>
                  {day.bookings.length > 0 && (
                    <ol className="mt-space-sm">
                      {day.bookings.slice(0, day.bookings.length === 3 ? 3 : 2).map((booking) => (
                        <li key={booking.id} className="truncate">
                          <button
                            type="button"
                            className={twResolve(
                              buttonVariants({ variant: "subtle", size: "sm" }),
                              "group h-fit items-center gap-space-sm",
                            )}
                            onClick={() => onSelectBookings([booking])}
                          >
                            <span className="hidden flex-none text-text-secondary @xl:block">
                              {booking.startTime.slice(0, 5)}
                            </span>
                            <p className="flex-auto truncate text-caption-emphasis text-text-primary">
                              {booking.author?.fullName ||
                                (booking.bookingReason === "External JIM booking"
                                  ? booking.bookingReason
                                  : t("page.bookings.asset-detail.author-fallback"))}
                            </p>
                          </button>
                        </li>
                      ))}
                      {day.bookings.length > MAX_AMOUNT_BOOKINGS_SHOWN && (
                        <li>
                          <button
                            type="button"
                            className={buttonVariants({ variant: "ghostSecondary", size: "sm" })}
                            onClick={() => onSelectBookings(day.bookings)}
                          >
                            {t("page.bookings.asset-detail.month-view.more", {
                              count: day.bookings.length - 2,
                            })}
                          </button>
                        </li>
                      )}
                    </ol>
                  )}
                </div>
              ))}
            </section>
            <section
              className={twJoin("grid w-full grid-cols-7 gap-px @md:hidden", has6Rows ? "grid-rows-6" : "grid-rows-5")}
            >
              {days.map((day) => (
                <button
                  key={day.date.toISOString()}
                  type="button"
                  className={twResolve(
                    buttonVariants({ variant: "subtle", size: "sm", fullWidth: true }),
                    day.isCurrentMonth ? "bg-background-default" : "bg-background-secondary",
                    (day.isSelected || day.isToday) && "text-body-emphasis",
                    day.isSelected && "text-text-default-inverse",
                    !day.isSelected && day.isToday && "text-text-primary",
                    !day.isSelected && day.isCurrentMonth && !day.isToday && "text-text-primary",
                    !day.isSelected && !day.isCurrentMonth && !day.isToday && "text-text-secondary",
                    "flex h-flow-header flex-col p-space-sm hover:bg-background-secondary focus:z-raised",
                  )}
                  onClick={() => selectDay(day)}
                >
                  <span
                    className={twJoin(
                      day.isSelected && "flex size-icon-prominent items-center justify-center rounded-full",
                      day.isSelected && day.isToday && "bg-background-brand-dark",
                      day.isSelected && !day.isToday && "bg-background-default-inverse",
                      "ml-auto",
                    )}
                  >
                    {day.date.getDate()}
                  </span>
                  <span className="sr-only">{day.bookings.length} events</span>
                  {day.bookings.length > 0 && (
                    <span className="-mx-space-xs mt-auto flex flex-wrap-reverse">
                      {day.bookings.map((event) => (
                        <span
                          key={event.id}
                          className="mx-space-xs mb-space-xs size-status-dot rounded-full bg-background-tertiary"
                        />
                      ))}
                    </span>
                  )}
                </button>
              ))}
            </section>
          </section>
        </section>
        {selectedDay && selectedDay.bookings.length > 0 && (
          <div className="px-space-md py-space-xl @sm:px-space-lg @md:hidden" ref={mobileBookingListRef}>
            <Card as="ol" density="flush" clip>
              {selectedDay.bookings.map((booking) => (
                <li
                  key={booking.id}
                  className="group flex flex-col border-b border-border-default/50 p-space-md pr-space-lg text-left text-caption last:border-b-0"
                >
                  <p className="flex items-center gap-space-sm text-body-emphasis text-text-primary">
                    {booking.author?.fullName ||
                      (booking.bookingReason === "External JIM booking"
                        ? booking.bookingReason
                        : t("page.bookings.asset-detail.author-fallback"))}
                  </p>
                  <span className="mt-space-sm flex items-center gap-space-sm text-text-secondary">
                    <ClockIcon size={20} aria-hidden="true" />
                    {booking.startTime.slice(0, 5)} - {booking.endTime.slice(0, 5)}
                  </span>
                </li>
              ))}
            </Card>
          </div>
        )}
      </section>
    </section>
  );
}
